import { Paths } from '../../../Routes/routePaths';

export const PERIOD_LABELS = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '365d': '12 months',
};

export const VALID_PERIODS = Object.keys(PERIOD_LABELS);

export const periodLabel = key => PERIOD_LABELS[key] || key;

const countFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const percentFormatter = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const formatCount = value => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return countFormatter.format(number);
};

export const formatDecimal = value => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return decimalFormatter.format(number);
};

export const formatPercent = value => (
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
    ? '-'
    : `${percentFormatter.format(Number(value))}%`
);

export const formatMetric = (value, unit) => (
  unit === 'percent' ? formatPercent(value) : formatCount(value)
);

export const isOverAssigned = (value, overAssigned) => !!overAssigned || Number(value) > 100;

const parseDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = value => {
  const date = parseDate(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(date);
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** `2026-11-01` -> "1 Nov 2026". Date-only values are read as local dates so they never shift by a day. */
export const formatCalendarDate = value => {
  const date = DATE_ONLY.test(String(value || '')) ? new Date(`${value}T00:00:00`) : parseDate(value);
  if (!date || Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(date);
};

/** Seconds -> "4 s", "3 min 5 s", "1 h 12 min". */
export const formatDuration = value => {
  if (value === null || value === undefined || value === '') return '-';
  const total = Math.round(Number(value));
  if (!Number.isFinite(total) || total < 0) return '-';
  if (total < 60) return `${total} s`;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (!hours) {
    const seconds = total % 60;
    return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
};

export const formatDateTime = value => {
  const date = parseDate(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

export const formatDay = value => {
  const date = parseDate(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short' }).format(date);
};

export const formatMonth = value => {
  if (!value) return '-';
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-ZA', { month: 'short', year: '2-digit' }).format(date);
};

const timeSpan = seconds => {
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
};

/** Past -> "2 hrs ago"; future (e.g. a next scheduled run) -> "in 40 min". */
export const relativeTime = value => {
  const date = parseDate(value);
  if (!date) return '-';
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  if (Math.abs(seconds) < 60) return 'just now';
  return seconds > 0 ? `in ${timeSpan(seconds)}` : `${timeSpan(-seconds)} ago`;
};

export const customerInsightsRoute = customerId => (
  customerId ? Paths.SuperadminCustomerInsights.replace(':customerId', encodeURIComponent(customerId)) : null
);

const CUSTOMER_DRILLDOWN = /superadmin_insights\/customers\/([^/?\s]+)/i;
const PROJECT_DRILLDOWN = /superadmin_insights\/projects\/([^/?\s]+)/i;

export const customerIdFromApi = (api = '') => {
  const match = CUSTOMER_DRILLDOWN.exec(String(api));
  return match ? decodeURIComponent(match[1]) : null;
};

/** `GET /main/superadmin_insights (sync_health)` points at a section on the dashboard itself. */
export const anchorForApi = (api = '') => {
  const match = /superadmin_insights\s*\(([a-z_]+)\)/i.exec(String(api));
  return match ? match[1] : null;
};

export const routeForApi = (api = '') => {
  const customerId = customerIdFromApi(api);
  if (customerId) return customerInsightsRoute(customerId);
  const projectMatch = PROJECT_DRILLDOWN.exec(String(api));
  if (projectMatch) return Paths.SuperadminProjectInsights.replace(':projectId', encodeURIComponent(decodeURIComponent(projectMatch[1])));
  const normalized = String(api).toLowerCase();
  if (normalized.includes('customer')) return Paths.Customers;
  if (normalized.includes('project')) return Paths.SuperadminProjects;
  if (normalized.includes('user')) return Paths.Users;
  if (normalized.includes('courserequest')) return Paths.CourseRequests;
  if (normalized.includes('course')) return Paths.Courses;
  if (normalized.includes('license') || normalized.includes('licence')) return Paths.Liceses;
  return Paths.Dashboard;
};

export const scrollToAnchor = anchor => {
  const target = anchor && document.getElementById(anchor);
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return !!target;
};

export const requestErrorMessage = error => {
  if (error?.code === 'ECONNABORTED') {
    return 'The server took too long to respond. Try again in a minute; the result is cached once it finishes.';
  }
  return error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Something went wrong';
};

const readBlobText = blob => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

/** Error bodies of blob requests arrive as a Blob; the JSON `message` is inside it. */
export const blobErrorMessage = async error => {
  const body = error?.response?.data;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      const parsed = JSON.parse(await readBlobText(body));
      if (parsed?.message || parsed?.detail) return parsed.message || parsed.detail;
    } catch (parseError) {
      // Not JSON: fall through to the generic message.
    }
  }
  return requestErrorMessage(error);
};
